import api from './api';

// Web Push on this device: the service worker (public/sw.js) shows the
// notifications, the backend encrypts them with its VAPID key.

export const SW_URL = '/sw.js';

export const isStandalone = () => Boolean(
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone,
);

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent || '')
  || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

// 'ok' | 'ios-install' (Safari on iOS only allows push from the home screen
// app) | 'unsupported'
export const pushSupport = () => {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) return 'ok';
  if (isIOS() && !isStandalone()) return 'ios-install';
  return 'unsupported';
};

export const permission = () => (typeof Notification === 'undefined' ? 'default' : Notification.permission);

export const urlBase64ToUint8Array = (value) => {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const sameKey = (buffer, key) => {
  if (!buffer) return false;
  const a = new Uint8Array(buffer);
  return a.length === key.length && a.every((v, i) => v === key[i]);
};

export const registerServiceWorker = () => (
  'serviceWorker' in navigator ? navigator.serviceWorker.register(SW_URL) : Promise.resolve(null)
);

export const currentSubscription = async () => {
  if (pushSupport() !== 'ok') return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
};

export class PushPermissionError extends Error {}

// subscribeDevice asks for the permission, subscribes the browser with the
// server key and registers the subscription on the backend.
export const subscribeDevice = async (publicKey) => {
  const granted = await Notification.requestPermission();
  if (granted !== 'granted') throw new PushPermissionError(granted);
  await registerServiceWorker();
  const reg = await navigator.serviceWorker.ready;
  const key = urlBase64ToUint8Array(publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options?.applicationServerKey, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const res = await api.post('/push/subscriptions', sub.toJSON());
  return { subscription: sub, devices: res.data?.devices };
};

export const unsubscribeDevice = async () => {
  const sub = await currentSubscription();
  if (!sub) return { devices: undefined };
  const res = await api.delete('/push/subscriptions', { data: { endpoint: sub.endpoint } });
  await sub.unsubscribe();
  return { devices: res.data?.devices };
};
